import type { AvailableUnitGroup } from '@/components/public/units';
import type { PublicPropertySummary, PublishedUnitSummary } from '@/lib/types/publicProperty';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import { calculatePublicMonthlyRent } from './publicRentCalculations';
import type { AvailabilityFiltersState, RangeBounds } from './availabilitiesFilters';
import { normalizeRange } from './availabilitiesFilters';

export function buildAssetTypeOptions(properties: PublicPropertySummary[]): string[] {
  const options = new Set<string>();
  properties.forEach((property) => {
    options.add(property.assetType);
  });
  return Array.from(options).sort();
}

export function sortAvailableGroups(groups: AvailableUnitGroup[]): AvailableUnitGroup[] {
  return [...groups].sort((a, b) => {
    const aPriority = isAllianceFirst(a.property) ? 0 : 1;
    const bPriority = isAllianceFirst(b.property) ? 0 : 1;
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    const aAddress = formatPropertyAddress(a.property.address).toLowerCase();
    const bAddress = formatPropertyAddress(b.property.address).toLowerCase();
    return aAddress.localeCompare(bAddress);
  });
}

export function filterAvailableGroups(
  groups: AvailableUnitGroup[],
  filters: AvailabilityFiltersState,
): AvailableUnitGroup[] {
  const [minRent, maxRent] = normalizeRange(filters.monthlyRent.min, filters.monthlyRent.max);
  const [minArea, maxArea] = normalizeRange(filters.areaSf.min, filters.areaSf.max);
  const hasUseFilter = filters.permittedUses.length > 0;

  return groups
    .map((group) => {
      if (hasUseFilter && !filters.permittedUses.includes(group.property.assetType)) {
        return null;
      }

      const units = group.units.filter((unit) => {
        const monthlyRent = getMonthlyRent(unit);
        if (minRent !== null && (monthlyRent === null || monthlyRent < minRent)) {
          return false;
        }
        if (maxRent !== null && (monthlyRent === null || monthlyRent > maxRent)) {
          return false;
        }
        if (minArea !== null && unit.areaSF < minArea) {
          return false;
        }
        if (maxArea !== null && unit.areaSF > maxArea) {
          return false;
        }
        return true;
      });

      if (units.length === 0) {
        return null;
      }

      return { ...group, units };
    })
    .filter(Boolean) as AvailableUnitGroup[];
}

export function buildAvailabilityRangeBounds(groups: AvailableUnitGroup[]): {
  monthlyRent: RangeBounds | null;
  areaSf: RangeBounds | null;
} {
  const rentValues: number[] = [];
  const areaValues: number[] = [];

  groups.forEach((group) => {
    group.units.forEach((unit) => {
      const monthlyRent = getMonthlyRent(unit);
      if (monthlyRent !== null) {
        rentValues.push(monthlyRent);
      }
      if (Number.isFinite(unit.areaSF)) {
        areaValues.push(unit.areaSF);
      }
    });
  });

  return {
    monthlyRent: resolveRangeBounds(rentValues, Math.floor, Math.ceil),
    areaSf: resolveRangeBounds(areaValues, Math.floor, Math.ceil),
  };
}

function isAllianceFirst(property: PublicPropertySummary): boolean {
  const address = formatPropertyAddress(property.address).toLowerCase();
  const code = property.propertyCode.toLowerCase();
  return address.includes('501 alliance') || code.includes('alliance') || code.includes('501');
}

function getMonthlyRent(unit: PublishedUnitSummary): number | null {
  return calculatePublicMonthlyRent(unit);
}

function resolveRangeBounds(
  values: number[],
  roundMin: (value: number) => number,
  roundMax: (value: number) => number,
): RangeBounds | null {
  if (values.length === 0) {
    return null;
  }

  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;
  values.forEach((value) => {
    if (!Number.isFinite(value)) {
      return;
    }
    if (value < minValue) {
      minValue = value;
    }
    if (value > maxValue) {
      maxValue = value;
    }
  });

  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return null;
  }

  const min = Math.max(0, roundMin(minValue));
  const max = Math.max(min, roundMax(maxValue));
  return { min, max };
}
